import React, { useState, useEffect, useCallback } from 'react';
import {
  Calculator, ArrowRight, Building2, User, Calendar, Clock,
  Euro, TrendingUp, Download, Trash2, Plus, CheckCircle, ChevronDown, Sparkles, Loader, FileSignature, PenLine,
} from 'lucide-react';
import { refacturationsApi, employesApi } from '../../utils/api';
import { useApp } from '../../context/AppContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const MOIS_LABELS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];
const eurf = (n) => (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

// Détection automatique des refacturations inter-structures (depuis le planning équipe)
function AnalyseAutoCard() {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [mois, setMois] = useState(prev.getMonth() + 1);
  const [annee, setAnnee] = useState(prev.getFullYear());
  const [lignes, setLignes] = useState(null); // refacturations du mois (toutes)
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadMois = useCallback(() => {
    refacturationsApi.getAll({ annee, mois }).then(r => setLignes(r.data || [])).catch(() => setLignes([]));
  }, [annee, mois]);

  const analyser = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await refacturationsApi.analyser(mois, annee);
      setMsg({ t: data.count ? 'ok' : 'info', m: data.count ? `${data.count} refacturation(s) détectée(s) et mise(s) en brouillon.` : 'Aucune refacturation inter-structures détectée pour ce mois.' });
      loadMois();
    } catch (e) { setMsg({ t: 'err', m: e.response?.data?.error || 'Analyse impossible.' }); }
    setBusy(false);
  };

  // Regroupe par (source → cible)
  const groupes = {};
  (lignes || []).forEach(l => { const k = `${l.structure_source_id}|${l.structure_cible_id}`; (groupes[k] = groupes[k] || { source: l.structure_source_id, cible: l.structure_cible_id, sourceNom: l.structure_source_nom, cibleNom: l.structure_cible_nom, lignes: [] }).lignes.push(l); });

  const signer = async (g) => {
    if (!window.confirm(`Signer électroniquement la convention de refacturation ${MOIS_LABELS[mois]} ${annee} (${g.sourceNom} → ${g.cibleNom}) ?`)) return;
    setBusy(true);
    try { await refacturationsApi.signer({ mois, annee, source: g.source, cible: g.cible }); setMsg({ t: 'ok', m: 'Convention signée électroniquement.' }); loadMois(); }
    catch (e) { setMsg({ t: 'err', m: e.response?.data?.error || 'Signature impossible.' }); }
    setBusy(false);
  };
  const valider = async (g) => {
    setBusy(true);
    try { for (const l of g.lignes.filter(x => x.statut === 'brouillon')) await refacturationsApi.valider(l.id); loadMois(); }
    catch (e) { setMsg({ t: 'err', m: e.response?.data?.error || 'Validation impossible.' }); }
    setBusy(false);
  };

  return (
    <div className="card p-5">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="p-2 bg-amber-50 rounded-lg"><Sparkles size={18} className="text-amber-600" /></div>
        <div>
          <div className="font-semibold text-gray-800">Analyse automatique des refacturations</div>
          <div className="text-xs text-gray-500">Détecte les salariées ayant travaillé dans une autre structure que la leur (planning équipe), génère les conventions en brouillon et vous alerte.</div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap mt-3">
        <select value={mois} onChange={e => setMois(Number(e.target.value))} className="form-select text-sm w-40">
          {MOIS_LABELS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
        </select>
        <input type="number" value={annee} onChange={e => setAnnee(Number(e.target.value))} className="form-input text-sm w-24" />
        <button onClick={analyser} disabled={busy} className="btn-primary flex items-center gap-1.5">{busy ? <Loader size={15} className="animate-spin" /> : <Sparkles size={15} />} Analyser le mois</button>
        {lignes === null && <button onClick={loadMois} className="btn-secondary text-sm">Voir ce mois</button>}
      </div>
      {msg && <div className={`mt-3 text-[13px] rounded-lg px-3 py-2 ${msg.t === 'err' ? 'bg-rose-50 text-rose-600' : msg.t === 'ok' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-50 text-gray-500'}`}>{msg.m}</div>}

      {lignes && lignes.length > 0 && (
        <div className="mt-4 space-y-3">
          {Object.values(groupes).map((g, gi) => {
            const total = g.lignes.reduce((s, l) => s + (l.cout_total_employeur || 0), 0);
            const signe = g.lignes.some(l => l.signee_at);
            const brouillon = g.lignes.some(l => l.statut === 'brouillon');
            return (
              <div key={gi} className="rounded-xl border border-cream-300 p-3">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <div className="text-sm font-medium text-navy-800 flex items-center gap-1.5">
                    <Building2 size={14} className="text-navy-400" /> {g.sourceNom} <ArrowRight size={13} className="text-navy-300" /> {g.cibleNom}
                    {signe ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">signée ✍️</span>
                      : brouillon ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">brouillon</span>
                        : <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">validée</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={refacturationsApi.getConventionPdfUrl({ mois, annee, source: g.source, cible: g.cible })} target="_blank" rel="noopener noreferrer" className="btn-secondary text-xs"><FileSignature size={14} /> Convention (PDF)</a>
                    {brouillon && <button onClick={() => valider(g)} disabled={busy} className="btn-secondary text-xs"><CheckCircle size={14} /> Valider</button>}
                    {!signe && <button onClick={() => signer(g)} disabled={busy} className="btn-primary text-xs"><PenLine size={14} /> Signer</button>}
                  </div>
                </div>
                <div className="space-y-1">
                  {g.lignes.map(l => (
                    <div key={l.id} className="flex items-center justify-between text-[13px] text-navy-600">
                      <span>{l.employe_prenom} {l.employe_nom} · {l.heures_effectuees} h</span>
                      <span className="font-medium">{eurf(l.cout_total_employeur)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between text-[13px] font-semibold text-navy-900 pt-1 border-t border-cream-200 mt-1">
                    <span>Total à refacturer</span><span>{eurf(total)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {lignes && lignes.length === 0 && <div className="mt-3 text-[13px] text-gray-400">Aucune refacturation enregistrée pour {MOIS_LABELS[mois]} {annee}.</div>}
    </div>
  );
}

function LigneCout({ label, montant, highlight, sublabel }) {
  return (
    <div className={`flex items-center justify-between py-2 ${highlight ? 'border-t-2 border-primary-200 mt-1 pt-3' : 'border-b border-gray-50'}`}>
      <div>
        <span className={`text-sm ${highlight ? 'font-bold text-gray-900' : 'text-gray-600'}`}>{label}</span>
        {sublabel && <div className="text-[10px] text-gray-400">{sublabel}</div>}
      </div>
      <span className={`font-mono ${highlight ? 'text-lg font-bold text-primary-600' : 'text-gray-800 font-medium'}`}>
        {montant != null ? montant.toFixed(2) + ' €' : '—'}
      </span>
    </div>
  );
}

export default function RefacturationPage() {
  const { structures } = useApp();
  const [employes, setEmployes] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHisto, setLoadingHisto] = useState(true);

  const now = new Date();
  const [form, setForm] = useState({
    employe_id: '',
    structure_source_id: '',
    structure_cible_id: '',
    mois: now.getMonth() + 1,
    annee: now.getFullYear(),
    heures_effectuees: '',
    notes: '',
  });
  const [simulation, setSimulation] = useState(null);
  const [saved, setSaved] = useState(false);
  const [filterAnnee, setFilterAnnee] = useState(now.getFullYear());
  const [showForm, setShowForm] = useState(true);

  useEffect(() => {
    employesApi.getAll({}).then(res => setEmployes(res.data)).catch(console.error);
  }, []);

  const loadHistorique = useCallback(() => {
    setLoadingHisto(true);
    refacturationsApi.getAll({ annee: filterAnnee })
      .then(res => setHistorique(res.data))
      .catch(console.error)
      .finally(() => setLoadingHisto(false));
  }, [filterAnnee]);

  useEffect(() => { loadHistorique(); }, [loadHistorique]);

  const employe = employes.find(e => String(e.id) === String(form.employe_id));

  const handleSimuler = async () => {
    if (!form.employe_id || !form.heures_effectuees) return;
    setLoading(true);
    setSaved(false);
    setSimulation(null);
    try {
      const res = await refacturationsApi.simuler({
        employe_id: form.employe_id,
        heures_effectuees: parseFloat(form.heures_effectuees),
      });
      setSimulation(res.data);
    } catch (err) {
      alert('Erreur lors du calcul');
    } finally {
      setLoading(false);
    }
  };

  const handleEnregistrer = async () => {
    if (!simulation || !form.employe_id || !form.heures_effectuees) return;
    setLoading(true);
    try {
      await refacturationsApi.create({
        ...form,
        heures_effectuees: parseFloat(form.heures_effectuees),
      });
      setSaved(true);
      loadHistorique();
    } catch (err) {
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette refacturation ?')) return;
    try {
      await refacturationsApi.delete(id);
      loadHistorique();
    } catch (err) {
      alert('Erreur');
    }
  };

  const handleFormChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setSimulation(null);
    setSaved(false);
  };

  const totalHistorique = historique.reduce((s, r) => s + (r.cout_total_employeur || 0), 0);

  return (
    <div className="space-y-5">

      {/* ── Analyse automatique (détection inter-structures) ── */}
      <AnalyseAutoCard />

      {/* ── Calculateur ── */}
      <div className="card">
        <button
          onClick={() => setShowForm(!showForm)}
          className="w-full flex items-center justify-between p-5 text-left"
        >
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-primary-50 rounded-lg">
              <Calculator size={18} className="text-primary-600" />
            </div>
            <div>
              <div className="font-semibold text-gray-800">Calculateur de refacturation</div>
              <div className="text-xs text-gray-500">Calculez le coût réel d'un salarié sur une autre structure</div>
            </div>
          </div>
          <ChevronDown size={18} className={`text-gray-400 transition-transform ${showForm ? 'rotate-180' : ''}`} />
        </button>

        {showForm && (
          <div className="px-5 pb-5 border-t border-gray-50">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">

              {/* Panneau gauche : formulaire */}
              <div className="space-y-4">

                {/* Employé */}
                <div>
                  <label className="form-label flex items-center gap-1.5">
                    <User size={13} className="text-primary-500" /> Employé
                  </label>
                  <select
                    value={form.employe_id}
                    onChange={e => handleFormChange('employe_id', e.target.value)}
                    className="form-select"
                  >
                    <option value="">— Sélectionner un employé —</option>
                    {employes.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.prenom} {e.nom} — {e.poste || 'Sans poste'}
                        {e.taux_horaire ? ` (${e.taux_horaire}€/h)` : ' ⚠ taux non défini'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Info employé sélectionné */}
                {employe && (
                  <div className="bg-navy-50 border border-navy-100 rounded-lg p-3 text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-navy-700">Structure habituelle</span>
                      <span className="font-medium text-navy-800">{employe.structure_nom || '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-navy-700">Taux horaire brut</span>
                      <span className="font-bold text-navy-800">{employe.taux_horaire ? `${employe.taux_horaire} €/h` : '⚠ Non défini'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-navy-700">Heures hebdo contrat</span>
                      <span className="font-medium text-navy-800">{employe.heures_hebdo_contrat || 35}h</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-navy-700">Charges patronales</span>
                      <span className="font-medium text-navy-800">{employe.taux_charges_patronales || 45}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-navy-700">Mutuelle patronale</span>
                      <span className="font-medium text-navy-800">{employe.mutuelle_part_patronale ? `${employe.mutuelle_part_patronale} €/mois` : '—'}</span>
                    </div>
                  </div>
                )}

                {/* Structures */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="form-label flex items-center gap-1.5">
                      <Building2 size={13} className="text-gray-400" /> Structure source
                    </label>
                    <select
                      value={form.structure_source_id}
                      onChange={e => handleFormChange('structure_source_id', e.target.value)}
                      className="form-select"
                    >
                      <option value="">— Auto —</option>
                      {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                    </select>
                    <p className="text-[10px] text-gray-400 mt-0.5">Où le salarié est rattaché</p>
                  </div>
                  <div>
                    <label className="form-label flex items-center gap-1.5">
                      <ArrowRight size={13} className="text-primary-500" /> Structure facturée
                    </label>
                    <select
                      value={form.structure_cible_id}
                      onChange={e => handleFormChange('structure_cible_id', e.target.value)}
                      className="form-select"
                    >
                      <option value="">— Sélectionner —</option>
                      {structures.map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
                    </select>
                    <p className="text-[10px] text-gray-400 mt-0.5">Qui doit payer</p>
                  </div>
                </div>

                {/* Période */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="form-label flex items-center gap-1.5">
                      <Calendar size={13} className="text-gray-400" /> Mois
                    </label>
                    <select
                      value={form.mois}
                      onChange={e => handleFormChange('mois', parseInt(e.target.value))}
                      className="form-select"
                    >
                      {MOIS_LABELS.slice(1).map((m, i) => (
                        <option key={i + 1} value={i + 1}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Année</label>
                    <input
                      type="number"
                      value={form.annee}
                      onChange={e => handleFormChange('annee', parseInt(e.target.value))}
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Heures */}
                <div>
                  <label className="form-label flex items-center gap-1.5">
                    <Clock size={13} className="text-primary-500" /> Heures effectuées sur la structure facturée
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={form.heures_effectuees}
                      onChange={e => handleFormChange('heures_effectuees', e.target.value)}
                      className="form-input flex-1"
                      placeholder="Ex: 40"
                    />
                    <button
                      onClick={handleSimuler}
                      disabled={!form.employe_id || !form.heures_effectuees || loading}
                      className="btn-primary whitespace-nowrap"
                    >
                      <Calculator size={15} />
                      {loading ? 'Calcul...' : 'Calculer'}
                    </button>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="form-label">Notes (optionnel)</label>
                  <input
                    value={form.notes}
                    onChange={e => handleFormChange('notes', e.target.value)}
                    className="form-input"
                    placeholder="Ex: Remplacement congés maladie"
                  />
                </div>
              </div>

              {/* Panneau droit : résultat */}
              <div>
                {!simulation && !loading && (
                  <div className="h-full flex flex-col items-center justify-center text-center py-10 text-gray-300">
                    <Calculator size={48} className="mb-3" />
                    <div className="text-sm text-gray-400">Sélectionnez un employé, saisissez les heures et cliquez sur Calculer</div>
                  </div>
                )}

                {loading && (
                  <div className="h-full flex items-center justify-center py-10 text-gray-400 text-sm">
                    Calcul en cours...
                  </div>
                )}

                {simulation && !loading && (
                  <div className="space-y-4">
                    {/* En-tête résultat */}
                    <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl p-4 border border-primary-100">
                      <div className="text-xs text-primary-500 font-medium mb-0.5">Période</div>
                      <div className="font-semibold text-gray-800 text-sm">
                        {MOIS_LABELS[form.mois]} {form.annee} — {form.heures_effectuees}h effectuées
                      </div>
                      {employe && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {employe.prenom} {employe.nom} · {simulation.taux_horaire_utilise} €/h brut
                        </div>
                      )}
                    </div>

                    {/* Détail du calcul */}
                    <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-3">Décomposition du coût</div>

                      <LigneCout
                        label="Salaire brut"
                        sublabel={`${form.heures_effectuees}h × ${simulation.taux_horaire_utilise} €/h`}
                        montant={simulation.salaire_brut}
                      />
                      <LigneCout
                        label="Charges patronales"
                        sublabel={`${simulation.taux_charges_patronales}% du brut`}
                        montant={simulation.charges_patronales}
                      />
                      {simulation.mutuelle_patronale > 0 && (
                        <LigneCout
                          label="Mutuelle (part patronale)"
                          sublabel={`Prorata ${simulation.prorata_mutuelle}% du mois`}
                          montant={simulation.mutuelle_patronale}
                        />
                      )}
                      <LigneCout
                        label="Coût total employeur"
                        montant={simulation.cout_total_employeur}
                        highlight
                      />
                    </div>

                    {/* Infos complémentaires */}
                    <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 space-y-1">
                      <div className="flex justify-between">
                        <span>Heures mensuelles contrat</span>
                        <span className="font-medium text-gray-700">{simulation.heures_mensuelles_contrat}h</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Coût horaire total employeur</span>
                        <span className="font-medium text-gray-700">
                          {form.heures_effectuees > 0
                            ? (simulation.cout_total_employeur / parseFloat(form.heures_effectuees)).toFixed(2) + ' €/h'
                            : '—'}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      {!saved ? (
                        <button
                          onClick={handleEnregistrer}
                          disabled={loading || !form.structure_cible_id}
                          className="btn-primary flex-1"
                          title={!form.structure_cible_id ? 'Sélectionnez la structure facturée' : ''}
                        >
                          <Plus size={15} />
                          {!form.structure_cible_id ? 'Choisir une structure cible' : 'Enregistrer la refacturation'}
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex-1 text-sm font-medium">
                          <CheckCircle size={16} />
                          Refacturation enregistrée
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Historique ── */}
      <div className="card">
        <div className="p-5 border-b flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-amber-50 rounded-lg">
              <TrendingUp size={16} className="text-amber-600" />
            </div>
            <div>
              <div className="font-semibold text-gray-800">Historique des refacturations</div>
              <div className="text-xs text-gray-500">
                {historique.length} refacturation{historique.length !== 1 ? 's' : ''} en {filterAnnee}
                {historique.length > 0 && (
                  <span className="ml-2 font-semibold text-gray-700">· Total : {totalHistorique.toFixed(2)} €</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={filterAnnee}
              onChange={e => setFilterAnnee(parseInt(e.target.value))}
              className="form-input w-24 text-sm"
            />
            <a
              href={refacturationsApi.getExportCsvUrl({ annee: filterAnnee })}
              className="btn-secondary text-xs"
              download
            >
              <Download size={14} /> Export CSV
            </a>
          </div>
        </div>

        {loadingHisto ? (
          <div className="py-12 text-center text-gray-400 text-sm">Chargement...</div>
        ) : historique.length === 0 ? (
          <div className="py-12 text-center">
            <Euro size={36} className="text-gray-200 mx-auto mb-3" />
            <div className="text-gray-400 text-sm">Aucune refacturation enregistrée pour {filterAnnee}</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Période</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Employé</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Source → Facturée</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Heures</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Brut</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Charges</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-600">Mutuelle</th>
                  <th className="text-right px-4 py-3 font-bold text-primary-600">Total employeur</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {historique.map((r, i) => (
                  <tr key={r.id} className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 !== 0 ? 'bg-gray-50/30' : ''}`}>
                    <td className="px-4 py-3 text-gray-700 font-medium whitespace-nowrap">
                      {MOIS_LABELS[r.mois]} {r.annee}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.employe_prenom} {r.employe_nom}</div>
                      <div className="text-xs text-gray-400">{r.employe_poste}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="bg-gray-100 rounded px-1.5 py-0.5">{r.structure_source_nom || '—'}</span>
                        <ArrowRight size={12} className="text-gray-400" />
                        <span className="bg-primary-50 text-primary-700 rounded px-1.5 py-0.5 font-medium">{r.structure_cible_nom || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">{r.heures_effectuees}h</td>
                    <td className="px-4 py-3 text-right text-gray-600 font-mono">{r.salaire_brut?.toFixed(2)} €</td>
                    <td className="px-4 py-3 text-right text-gray-600 font-mono">{r.charges_patronales?.toFixed(2)} €</td>
                    <td className="px-4 py-3 text-right text-gray-600 font-mono">
                      {r.mutuelle_patronale > 0 ? `${r.mutuelle_patronale?.toFixed(2)} €` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-primary-600 font-mono">
                      {r.cout_total_employeur?.toFixed(2)} €
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDelete(r.id)}
                        className="p-1.5 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-primary-50 border-t-2 border-primary-200">
                  <td colSpan={7} className="px-4 py-3 text-sm font-semibold text-gray-700">Total {filterAnnee}</td>
                  <td className="px-4 py-3 text-right font-bold text-primary-700 font-mono text-base">
                    {totalHistorique.toFixed(2)} €
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
